/**
 * Shared, host-independent form submission service.
 *
 * - Posts to the configurable endpoint (see src/config/forms.ts).
 * - Builds the payload straight from the real <form> element, so browser
 *   autofill values are captured (component state can miss them).
 * - Handles the anti-spam honeypot.
 * - Sends `application/x-www-form-urlencoded` and expects a JSON reply:
 *       { "success": true, "code": "OK", "message": "..." }
 * - Never treats a server response containing "spam" as success.
 */
import { FORM_ENDPOINT } from "@/config/forms";
import { getAntiBotPayload } from "@/services/antiBot";
import { sanitize } from "@/utils/validation";
import { contactInfo } from "@/mocks/site";

export interface SubmitFormOptions {
  /** Override the endpoint for this form. Defaults to the global config. */
  endpoint?: string;
  /** Honeypot field name to strip from the payload. */
  honeypotField?: string;
}

export interface SubmitResult {
  status: "success" | "error";
  message: string;
}

const SUCCESS_MESSAGE = "Thank you! Our team will reach out to you shortly.";
const GENERIC_ERROR = `Something went wrong. Please try again or call us at ${contactInfo.phone}.`;
const NETWORK_ERROR = `Network error. Please try again or call us at ${contactInfo.phone}.`;

function isSuccessPayload(parsed: Record<string, unknown> | null, response: Response): boolean {
  if (!response.ok || !parsed) return false;
  return parsed.success === true || parsed.code === "OK" || parsed.status === "success";
}

export async function submitForm(
  form: HTMLFormElement,
  options: SubmitFormOptions = {},
): Promise<SubmitResult> {
  const endpoint = (options.endpoint || FORM_ENDPOINT).trim();
  const honeypotField = options.honeypotField || "company_alt";

  const formData = new FormData(form);

  // Honeypot: only bots fill it. Pretend success so they never learn.
  const honeypot = String(formData.get(honeypotField) || "").trim();
  if (honeypot) {
    return { status: "success", message: SUCCESS_MESSAGE };
  }

  const params = new URLSearchParams();
  formData.forEach((value, key) => {
    if (key === honeypotField) return;
    if (typeof value !== "string") return;
    params.append(key, sanitize(value, key === "message" ? 1000 : 180));
  });

  // Optional anti-bot token (no-op when not configured).
  try {
    const antiBot = await getAntiBotPayload();
    Object.entries(antiBot).forEach(([key, value]) => params.append(key, value));
  } catch {
    /* ignore — the server decides */
  }

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: params.toString(),
    });
  } catch {
    return { status: "error", message: NETWORK_ERROR };
  }

  // Always read the body as text first, then try to parse JSON.
  const responseText = await response.text();
  let parsed: Record<string, unknown> | null = null;
  try {
    const decoded = JSON.parse(responseText) as unknown;
    parsed = decoded && typeof decoded === "object" ? (decoded as Record<string, unknown>) : null;
  } catch {
    parsed = null;
  }

  const meta = (parsed?.meta && typeof parsed.meta === "object" ? parsed.meta : {}) as Record<string, unknown>;
  const candidate = parsed?.message ?? meta.message ?? meta.detail ?? parsed?.error;
  const serverMsg =
    typeof candidate === "string" && candidate.trim().length > 0
      ? candidate.trim()
      : typeof responseText === "string"
        ? responseText.trim()
        : "";

  const isSpam = serverMsg.toLowerCase().includes("spam");
  const ok = isSuccessPayload(parsed, response) && !isSpam;

  if (ok) {
    return { status: "success", message: SUCCESS_MESSAGE };
  }

  // Only surface a server message if it looks like a short, human message.
  const safeServerMessage = serverMsg.length > 0 && serverMsg.length <= 200 && !serverMsg.includes("<");
  return {
    status: "error",
    message: safeServerMessage ? serverMsg : GENERIC_ERROR,
  };
}